/**
 * Accounts Domain — Public API (Barrel Export)
 *
 * This is the public interface for the accounts domain.
 * All account-related imports should come through this barrel.
 *
 * Split from the 878-line accountHelpers.ts god file into 5 focused modules (R18):
 * - creation.ts: Account creation & ID generation
 * - queries.ts: Lookups, getters, validation
 * - updates.ts: Field updates & deletion
 * - relationships.ts: Parent/teacher linking
 * - classrooms.ts: Classroom CRUD
 *
 * Additional modules consolidated (R19):
 * - migration.ts: V3 state load/save/create (from utils/accountMigration.ts)
 * - auth.ts: Password, session, login helpers (from utils/authHelpers.ts)
 * - uiHelpers.ts: Role icons, labels, colors (from utils/accountUIHelpers.ts)
 *
 * Types added (R30): Account types moved from types/accounts.ts
 */

// ── Creation ─────────────────────────────────────────────────
export { generateAccountId, createDefaultAdminAccount, ensureAdminAccount, createAccountInState } from './logic/creation';

// ── Queries ──────────────────────────────────────────────────
export { getActiveAccount, getAccountById, switchActiveAccount, getLinkedLearners, getLinkedParents, getContextLearnerAccount, validateAccountsState, getUniqueSchoolNames } from './logic/queries';

// ── Updates ──────────────────────────────────────────────────
export { updateAccountName, updateAccountEmail, updateAccountPassword, updateTeacherDetails, updateLearnerLinkCode, updateLearnerGrade, updateLearnerDetails, touchLastActive, deleteAccountFromState, completeOnboarding, prepareAccountDeletion, executeAccountDeletion, validateEmailUpdate, ORPHAN_TEACHER_PREFIX, isOrphanedTeacherId } from './logic/updates';
export type { AccountDeletionDecision, AccountDeletionPreparation } from './logic/updates';

// ── Relationships ────────────────────────────────────────────
export { MAX_PARENTS_PER_LEARNER, linkParentToLearner, linkTeacherToLearner, unlinkParentFromLearner, unlinkTeacherFromLearner, restoreArchivedLearner } from './logic/relationships';

// ── Classrooms ───────────────────────────────────────────────
export { generateClassroomId, generateJoinCode, JOIN_CODE_LENGTH, JOIN_CODE_EXAMPLE, generateUniqueJoinCode, generateCoTeacherCode, generateUniqueCoTeacherCode, CO_TEACHER_CODE_LENGTH, findClassroomByCoTeacherCode, regenerateCoTeacherCode, getTeacherClassrooms, getOwnedClassrooms, getClassroomRole, MAX_CO_TEACHERS_PER_CLASSROOM, findClassroomByJoinCode, addClassroom, updateClassroom, removeClassroom, addLearnerToClassroom, removeLearnerFromClassroom, getClassroomStudents, joinClassroomByCode, regenerateJoinCode, findDuplicateClassroomName, getLearnerExistingClassrooms, validateAddLearnerToClassroom, validateNewClassroom, getSchoolClassrooms, getAllClassroomsWithTeachers, transferClassroomOwnership, joinClassroomAsCoTeacher, leaveClassroomAsCoTeacher } from './logic/classrooms';
export type { ClassroomWithTeacher } from './logic/classrooms';
export type { ClassroomValidationWarning } from './logic/classrooms';

// ── Orphans ──────────────────────────────────────────────────
export { detectOrphanedClassrooms, cleanupOrphans } from './logic/orphans';
export type { OrphanReport } from './logic/orphans';

// ── Unified Linkage (canonical — replaces validateAddLearnerToClassroom +
//    getLearnerClassroomFlags + ad-hoc parent validation) ─────────────
export { validateLink, getLearnerLinkageFlags, hasAnyLinkageFlag, hasGatingWarning, hasBlockingWarning } from './logic/learnerLinkage';
export type { LinkContext, LinkWarning, LinkWarningSeverity, LearnerLinkageFlags, LinkageFlagsOptions } from './logic/learnerLinkage';

// ── Permissions ──────────────────────────────────────────────
export { canPerform, getAllowedActions, roleHasPermission } from './logic/permissions';
export type { PermissionAction, PermissionContext } from './logic/permissions';

// ── Migration (V4 State — current) ──────────────────────────
export { CURRENT_SCHEMA_VERSION, loadState, createFreshState, saveState } from './logic/migration';
export type { MultiAccountStateV4 } from './logic/migration';

// ── Auth Helpers ─────────────────────────────────────────────
export { generateLinkCode, LINK_CODE_LENGTH, LINK_CODE_EXAMPLE, findAccountByLinkCode, findAccountByUsername, findAccountByEmail, suggestUsername, isValidUsername, isValidPassword, isUsernameAvailable, isEmailAvailable, generateUniqueLinkCode, sanitizeDisplayName, sanitizeEmail, validateRegistrationInput } from './logic/auth';

// ── Auth Service ─────────────────────────────────────────────
//
// VERWIJDERD (augustus 2026). `logic/authService.ts` was een volledige
// tweede inlogimplementatie naast Supabase: eigen sessies, eigen
// ratelimiet, eigen wachtwoordverificatie. Sinds de omzetting werd geen
// enkele functie daaruit nog aangeroepen.
//
// Het probleem was niet de dode code maar de naamgenoten. `loginWithEmail`
// bestond twee keer, met dezelfde signatuur en ander gedrag. Wie de
// verkeerde importeerde kreeg code die compileert, draait en niets doet —
// en dat is bij inloggen niet te zien tot iemand niet binnenkomt.
//
// Authenticatie loopt nu uitsluitend via `src/data/supabase/authService.ts`.
//
// NAGEKOMEN (12 augustus 2026): bij het schrijven van deze notitie is alleen de
// export hier weggehaald; het bestand zelf bleef staan — 525 regels met
// wachtwoordhashing, ratelimiet en sessiebeheer, met nul importeurs. Nu echt
// verwijderd. "Verwijderd" moet betekenen dat het bestand weg is: een
// niet-geëxporteerd bestand is één import verwijderd van precies de
// naamgenoot-verwarring die hierboven beschreven staat.
//
// Ditzelfde gold voor `src/demo/` (8 bestanden, ~600 regels) — óók op dezelfde
// dag echt verwijderd. Vier keer hetzelfde patroon betekent dat er een sweep op
// nul-importeurs hoort te bestaan, niet dat er vier keer iemand slordig was.

// ── UI Helpers ───────────────────────────────────────────────
export { getAccountIcon, getAccountRoleLabel, ACCOUNT_ROLE_COLORS } from './logic/uiHelpers';

// ── Mastery Queries (pure computation helpers) ───────────────
//
// De verwijzing naar `src/demo/` die hier stond, is vervallen: die map bestaat
// niet meer (demo-modus uitgefaseerd, augustus 2026).
export { createMasteryQueries } from './hooks/useLearnerActions';

// ── UI Components ────────────────────────────────────────────
export { LoginScreen } from './components/LoginScreen';
export { RegisterScreen } from './components/RegisterScreen';
export { WelcomeScreen } from './components/WelcomeScreen';
// `AccountSwitcherDropdown` én `AccountSwitcher` stonden hier. Beide verwijderd
// (aug 2026), zie SHARED_DEVICE_AND_SIBLINGS.md.
//
// Ze deelden dezelfde aanname: er is een lijst van accounts op dít apparaat, en
// wisselen is het aanwijzen van een ander item in die lijst. Sinds auth
// serverside loopt bestaat die lijst niet meer — er is één sessie per keer, en
// een andere gebruiker worden betekent opnieuw authenticeren.
//
// Wat wél overeind blijft is het multi-role-scenario (ouder én leerkracht op
// één adres). Dat is géén accountwissel maar een portaalwissel binnen dezelfde
// sessie: één identiteit, twee rollen. Die knop hoort in de kopbalk van het
// portaal en leest de rollen uit het serverprofiel — nieuw werk, niet het
// herstellen van deze component.
export { ProfileButton } from './components/ProfileButton';
export { ProfileSection } from './components/ProfileSection';

// ── Onboarding Components ───────────────────────────────────
// Note: JoinClassroomPrompt / LinkChildPrompt / CreateFirstClassroom zijn
// verwijderd in Sprint 2.8 (zie AUTH_ONBOARDING_DOMAIN_RC_AUDIT_2026.md §23).
// Alle child/classroom-linking gebeurt nu in de portal-empty-states.
export { OnboardingWizard } from './components/OnboardingWizard';
export { SetNewPasswordScreen } from './components/SetNewPasswordScreen';
export { isPasswordRecoveryLanding, getRecoveryLinkError, clearRecoveryHash } from './logic/recoveryLink';
export { OnboardingProgress, OnboardingWelcomeStep } from './components/onboarding';

// ── Types (canonical source — moved from types/accounts.ts in R30) ───────────
export type { AccountRole, LearnerAccount, ParentAccount, Classroom, TeacherAccount, AdminAccount, Account, RelationshipGraph, AccountsState, ActiveContext, OnboardingStepState } from './types';
export { isLearnerAccount, isParentAccount, isTeacherAccount, isAdminAccount } from './types';